import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function VendorRegistration() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    companyType: '',
    yearsInBusiness: '',
    productCategories: [] as string[],
    otherCategory: '',
    annualRevenue: '',
    numberOfEmployees: '',
    manufacturingCapability: '',
    certifications: '',
    primaryMarkets: '',
    distributionChannels: '',
    minimumOrderValue: '',
    leadTime: '',
    paymentTerms: '',
    shippingCapabilities: '',
    qualityControl: '',
    references: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const productCategoryOptions = [
    'Batteries (Automotive, Industrial, Marine, Powersports)',
    'Battery Chargers',
    'Battery Storage Systems',
    'Power Tools',
    'Safety Equipment',
    'Diagnostic Tools',
    'Electrical Components',
    'Marine Equipment',
    'Industrial Equipment',
    'Home Improvement',
    'Electronics',
    'Healthcare',
    'Plumbing',
    'Sports and Outdoors',
    'Other'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'additionalInfo') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (category: string) => {
    setFormData(prev => ({
      ...prev,
      productCategories: prev.productCategories.includes(category)
        ? prev.productCategories.filter(c => c !== category)
        : [...prev.productCategories, category]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('contactName', formData.contactName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('website', formData.website);
      formBody.append('companyType', formData.companyType);
      formBody.append('yearsInBusiness', formData.yearsInBusiness);
      formBody.append('productCategories', formData.productCategories.join(', '));
      if (formData.otherCategory) {
        formBody.append('otherCategory', formData.otherCategory);
      }
      formBody.append('annualRevenue', formData.annualRevenue);
      formBody.append('numberOfEmployees', formData.numberOfEmployees);
      formBody.append('manufacturingCapability', formData.manufacturingCapability);
      formBody.append('certifications', formData.certifications);
      formBody.append('primaryMarkets', formData.primaryMarkets);
      formBody.append('distributionChannels', formData.distributionChannels);
      formBody.append('minimumOrderValue', formData.minimumOrderValue);
      formBody.append('leadTime', formData.leadTime);
      formBody.append('paymentTerms', formData.paymentTerms);
      formBody.append('shippingCapabilities', formData.shippingCapabilities);
      formBody.append('qualityControl', formData.qualityControl);
      formBody.append('references', formData.references);
      if (formData.additionalInfo) {
        formBody.append('additionalInfo', formData.additionalInfo);
      }

      const response = await fetch('https://readdy.ai/api/form/d5ueljus8bb84h5iedjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          companyType: '',
          yearsInBusiness: '',
          productCategories: [],
          otherCategory: '',
          annualRevenue: '',
          numberOfEmployees: '',
          manufacturingCapability: '',
          certifications: '',
          primaryMarkets: '',
          distributionChannels: '',
          minimumOrderValue: '',
          leadTime: '',
          paymentTerms: '',
          shippingCapabilities: '',
          qualityControl: '',
          references: '',
          additionalInfo: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-20 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Become a Vendor Partner
            </h1>
            <p className="text-xl text-teal-50 leading-relaxed max-w-3xl mx-auto">
              Join our network of trusted suppliers and manufacturers. We're always looking for quality partners who share our commitment to excellence and reliability.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <form id="vendor-registration-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Company Information */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Company Information</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-semibold text-slate-700 mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactName" className="block text-sm font-semibold text-slate-700 mb-2">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="website" className="block text-sm font-semibold text-slate-700 mb-2">
                      Company Website
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="https://www.company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="companyType" className="block text-sm font-semibold text-slate-700 mb-2">
                      Company Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="companyType"
                      name="companyType"
                      value={formData.companyType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option value="Manufacturer">Manufacturer</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Wholesaler">Wholesaler</option>
                      <option value="Importer">Importer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="yearsInBusiness" className="block text-sm font-semibold text-slate-700 mb-2">
                      Years in Business <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="yearsInBusiness"
                      name="yearsInBusiness"
                      value={formData.yearsInBusiness}
                      onChange={handleInputChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="5"
                    />
                  </div>

                  <div>
                    <label htmlFor="numberOfEmployees" className="block text-sm font-semibold text-slate-700 mb-2">
                      Number of Employees <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="numberOfEmployees"
                      name="numberOfEmployees"
                      value={formData.numberOfEmployees}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select range</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Product Information */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                    <i className="ri-box-3-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Product Information</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Product Categories <span className="text-red-500">*</span>
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {productCategoryOptions.map((category) => (
                        <label key={category} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                          <input
                            type="checkbox"
                            name="productCategories"
                            checked={formData.productCategories.includes(category)}
                            onChange={() => handleCheckboxChange(category)}
                            className="w-5 h-5 text-teal-600 border-slate-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="text-sm text-slate-700">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.productCategories.includes('Other') && (
                    <div>
                      <label htmlFor="otherCategory" className="block text-sm font-semibold text-slate-700 mb-2">
                        Please Specify Other Category
                      </label>
                      <input
                        type="text"
                        id="otherCategory"
                        name="otherCategory"
                        value={formData.otherCategory}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                        placeholder="Describe your product category"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="manufacturingCapability" className="block text-sm font-semibold text-slate-700 mb-2">
                      Manufacturing Capability <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="manufacturingCapability"
                      name="manufacturingCapability"
                      value={formData.manufacturingCapability}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., 10,000 units per month"
                    />
                  </div>

                  <div>
                    <label htmlFor="certifications" className="block text-sm font-semibold text-slate-700 mb-2">
                      Certifications &amp; Compliance
                    </label>
                    <input
                      type="text"
                      id="certifications"
                      name="certifications"
                      value={formData.certifications}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., ISO 9001, UL, CE"
                    />
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                    <i className="ri-line-chart-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Business Details</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="annualRevenue" className="block text-sm font-semibold text-slate-700 mb-2">
                      Annual Revenue Range
                    </label>
                    <select
                      id="annualRevenue"
                      name="annualRevenue"
                      value={formData.annualRevenue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select range</option>
                      <option value="Under $500K">Under $500K</option>
                      <option value="$500K - $1M">$500K - $1M</option>
                      <option value="$1M - $5M">$1M - $5M</option>
                      <option value="$5M - $10M">$5M - $10M</option>
                      <option value="$10M+">$10M+</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="primaryMarkets" className="block text-sm font-semibold text-slate-700 mb-2">
                      Primary Markets <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="primaryMarkets"
                      name="primaryMarkets"
                      value={formData.primaryMarkets}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., North America, Europe"
                    />
                  </div>

                  <div>
                    <label htmlFor="distributionChannels" className="block text-sm font-semibold text-slate-700 mb-2">
                      Distribution Channels <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="distributionChannels"
                      name="distributionChannels"
                      value={formData.distributionChannels}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., Direct, Distributors, Online"
                    />
                  </div>

                  <div>
                    <label htmlFor="minimumOrderValue" className="block text-sm font-semibold text-slate-700 mb-2">
                      Minimum Order Value <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="minimumOrderValue"
                      name="minimumOrderValue"
                      value={formData.minimumOrderValue}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., $500"
                    />
                  </div>

                  <div>
                    <label htmlFor="leadTime" className="block text-sm font-semibold text-slate-700 mb-2">
                      Typical Lead Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="leadTime"
                      name="leadTime"
                      value={formData.leadTime}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., 2-4 weeks"
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentTerms" className="block text-sm font-semibold text-slate-700 mb-2">
                      Payment Terms <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="paymentTerms"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., Net 30, Net 60"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="shippingCapabilities" className="block text-sm font-semibold text-slate-700 mb-2">
                      Shipping Capabilities <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="shippingCapabilities"
                      name="shippingCapabilities"
                      value={formData.shippingCapabilities}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="e.g., LTL, FTL, International"
                    />
                  </div>
                </div>
              </div>

              {/* Quality &amp; References */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Quality &amp; References</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="qualityControl" className="block text-sm font-semibold text-slate-700 mb-2">
                      Quality Control Process <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="qualityControl"
                      name="qualityControl"
                      value={formData.qualityControl}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder="Describe your quality control procedures and standards"
                    ></textarea>
                  </div>

                  <div>
                    <label htmlFor="references" className="block text-sm font-semibold text-slate-700 mb-2">
                      Business References
                    </label>
                    <textarea
                      id="references"
                      name="references"
                      value={formData.references}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder="List any current or past business references (company names and contact information)"
                    ></textarea>
                  </div>

                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-semibold text-slate-700 mb-2">
                      Additional Information
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder="Any additional information you'd like to share about your company"
                    ></textarea>
                    <div className="mt-2 text-right">
                      <span className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-slate-500'}`}>
                        {charCount}/500 characters
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col items-center gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.productCategories.length === 0}
                  className="whitespace-nowrap px-12 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-lg font-bold rounded-xl hover:from-teal-700 hover:to-teal-800 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-3">
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      <i className="ri-send-plane-line text-xl"></i>
                      Submit Application
                    </span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 max-w-2xl w-full">
                    <div className="flex items-center gap-3">
                      <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                      <div>
                        <h4 className="font-bold text-green-900 mb-1">Application Submitted Successfully!</h4>
                        <p className="text-sm text-green-700">
                          Thank you for your interest in partnering with DFF Distribution. We'll review your application and contact you within 3-5 business days.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 max-w-2xl w-full">
                    <div className="flex items-center gap-3">
                      <i className="ri-error-warning-line text-2xl text-red-600"></i>
                      <div>
                        <h4 className="font-bold text-red-900 mb-1">Submission Failed</h4>
                        <p className="text-sm text-red-700">
                          There was an error submitting your application. Please try again or contact us directly at CustomerSupport@dffdistribution.com
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white py-20 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Why Partner With Us?
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Join a growing network of suppliers serving institutional and municipal markets
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100/50">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-government-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Institutional Access</h3>
                <p className="text-slate-600 leading-relaxed">
                  Reach government agencies, municipalities, and institutional buyers through our established channels
                </p>
              </div>

              <div className="text-center p-8 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/50">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Dedicated Support</h3>
                <p className="text-slate-600 leading-relaxed">
                  Work with experienced professionals who understand supply chain and compliance requirements
                </p>
              </div>

              <div className="text-center p-8 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100/50">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Growth Opportunities</h3>
                <p className="text-slate-600 leading-relaxed">
                  Expand your market presence with consistent order volume and long-term partnerships
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
